/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AelDto, BuilderPermissionSet, SystemRoleBit } from '@gh/builder-shared-data';
import {
  AppRouter,
  filterableForm,
  FilterableFormState,
  FormStateBuilder,
  getRouteInitData,
  getRouteParentTitle,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { filterTracker, RouteDataService, SlicedDataSource } from '@gh/core-data';
import { TranslationService, TranslationTupleOrId } from '@gh/core-mls';
import { AelListBusinessService } from '../../resources/ael-list-business.service';
import { AelGridDefinition } from './ael-grid-definition.service';
import { cursorTracker, newGridPaginationCursor } from '@gh/core-ui-grid';
import { padStart, replace } from 'lodash';
import { currentDate, openInThisWindow, toString } from '@gh/core-util';
import { BuilderUserContext } from '@gh/builder-shared';

@Component({
  standalone: false,
  selector: 'gh-ael-list',
  templateUrl: 'ael-list.component.html',
  styleUrls: ['ael-list.component.scss'],
  viewProviders: [AelGridDefinition],
})
export class AelListComponent implements OnInit {
  parentRoute: string;
  parentLabel: TranslationTupleOrId;
  dataSource: SlicedDataSource<AelDto>;
  entityType?: string;
  entityId?: ID;
  eventTypeIds?: number[];
  back: boolean = false;
  form: FilterableFormState<any>;
  cursor = newGridPaginationCursor();
  defaultStartDate = currentDate();
  eventTypeIdList = {
    JOB: [121, 122, 123, 131, 132, 144, 150, 151, 152, 198, 214, 215, 216, 252, 274, 275, 276, 277, 278, 290, 293],
    LEAD: [81, 82],
    SALES_FOLDER: [83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96, 127, 128],
    QUOTE: [97, 98, 99, 100, 101, 199, 200],
    CONTACT: [18, 19],
    HLP:  [266, 267, 269, 270, 271, 272, 273],
    PB: [67, 68, 136, 137, 253],
    HOUSE_PLAN: [70, 102, 105, 207],
    JOB_SPEC: [124, 125],
    JOB_EST: [129, 130],
    JOB_CS: [133, 239],
    JOB_VAR: [138, 139, 140, 141, 142, 197, 221, 222, 223, 224, 225, 226 ],
    SCHEDULES_CONSTRUCTOR: [189, 190 ],
    SCHEDULES_SALES: [191, 192],
    SCHEDULES_PRE_CONSTRUCTOR: [ 193, 194 ],
    SCHEDULES_MAINTENANCE: [195, 196],
  };
  ALL_ENTITY_TYPE_ID = [
    ...this.eventTypeIdList.JOB,
    ...this.eventTypeIdList.JOB_CS,
    ...this.eventTypeIdList.JOB_EST,
    ...this.eventTypeIdList.JOB_SPEC,
    ...this.eventTypeIdList.JOB_VAR,
    ...this.eventTypeIdList.LEAD,
    ...this.eventTypeIdList.SALES_FOLDER,
    ...this.eventTypeIdList.QUOTE,
    ...this.eventTypeIdList.CONTACT,
    ...this.eventTypeIdList.HLP,
    ...this.eventTypeIdList.PB,
    ...this.eventTypeIdList.HOUSE_PLAN,
    ...this.eventTypeIdList.SCHEDULES_CONSTRUCTOR,
    ...this.eventTypeIdList.SCHEDULES_PRE_CONSTRUCTOR,
    ...this.eventTypeIdList.SCHEDULES_SALES,
    ...this.eventTypeIdList.SCHEDULES_MAINTENANCE,
  ];
  defaultStartDateStr = `${currentDate().getFullYear()}/${currentDate().getMonth() + 1}/${currentDate().getDate()}`;
  initialFilter = {
    searchString: '',
    startDate: new Date(this.defaultStartDate.setDate(this.defaultStartDate.getDate() - 14))
      .toISOString().split('T')[0],
    endDate: new Date(this.defaultStartDateStr),
  };

  PERMISSIONS = {
    JOB: this.userContext.hasPermission(BuilderPermissionSet.JobAdmin.JobDetails.ANY),
    JOB_EST: this.userContext.hasPermission(BuilderPermissionSet.Estimating.JobEstimate.EDIT_VIEW),
    JOB_CS: this.userContext.hasPermission(BuilderPermissionSet.JobAdmin.JobColorSelection.ANY),
    JOB_VAR: this.userContext.hasPermission(BuilderPermissionSet.JobAdmin.JobChangeOrders.ANY),
    JOB_SPEC: this.userContext.hasPermission(BuilderPermissionSet.JobAdmin.JobSpecifications.ANY),
    SALES_FOLDER: this.userContext.hasPermission(BuilderPermissionSet.Sales.Leads.ANY),
    QUOTE: this.userContext.hasPermission(BuilderPermissionSet.Sales.Leads.ANY),
    SCHEDULES_SALES: this.userContext.hasPermission(BuilderPermissionSet.Schedules.Sales.EDIT_VIEW),
    SCHEDULES_MAINTENANCE: this.userContext.hasPermission(BuilderPermissionSet.Schedules.Maintenance.EDIT_VIEW),
    SCHEDULES_CONSTRUCTOR: this.userContext.hasPermission(BuilderPermissionSet.Schedules.Construction.EDIT_VIEW),
    SCHEDULES_PRE_CONSTRUCTOR: this.userContext.hasPermission(BuilderPermissionSet.Schedules.PreConstruction.EDIT_VIEW),
    LEAD: this.userContext.hasPermission(BuilderPermissionSet.Sales.Leads.ANY),
    CONTACT: this.userContext.hasPermission(BuilderPermissionSet.Contacts.Details.ANY),
    HLP: this.userContext.hasPermission(BuilderPermissionSet.Sales.Hlp.ANY),
    PB: this.userContext.hasPermission(BuilderPermissionSet.Estimating.PriceBook.CREATE_EDIT_VIEW),
    HOUSE_PLAN: this.userContext.hasPermission(BuilderPermissionSet.Estimating.HouseEstimate.Plans.CREATE_EDIT_VIEW),
  };

  constructor(private businessService: AelListBusinessService,
              private translation: TranslationService,
              private userContext: BuilderUserContext,
              private route: ActivatedRoute,
              private router: AppRouter,
              private routeData: RouteDataService,
              private formStateBuilder: FormStateBuilder,
              public gridDefinition: AelGridDefinition) {
  }

  /**
   * Init component
   */
  ngOnInit(): void {
    const { gridLayout } = getRouteInitData(this.route);

    this.gridDefinition.bind(gridLayout);

    this.route.queryParams.subscribe((params) => {
      this.parentLabel = getRouteParentTitle(this.route) || 'lbl_application_event_log';
      this.parentRoute = params['parentRoute'];
      this.entityType = params['entityType'];
      this.entityId = params['entityId'];
      this.eventTypeIds = params['eventTypeIds'] && params['eventTypeIds'].split(',');
      if (this.parentRoute) {
        this.back = true;
      }
    });

    this.form = filterableForm(this.formStateBuilder.buildPlain({
      value: this.initialFilter,
      children: {
        searchString: void 0,
        startDate: void 0,
        endDate: void 0,
      },
    }));

    const tracker = this.routeData.tracker(filterTracker(this.form))
      .withEffects(cursorTracker(this.cursor));
    tracker.listen(this.route);
    tracker.filterChanges.subscribe(([filter]) => {
      const endDate = new Date(filter.endDate);
      const startDate = new Date(filter.startDate);
      const fixedFilter = {
        searchString: filter.searchString || '',
        startDate: filter.startDate ? `${[startDate.getFullYear(), startDate.getMonth() + 1, startDate.getDate()].map(this.stringifyDateComponent).join('-')}T00:00:00` : null,
        endDate: filter.endDate ? `${[endDate.getFullYear(), endDate.getMonth() + 1, endDate.getDate()].map(this.stringifyDateComponent).join('-')}T23:59:59` : null,
      };
      this.dataSource = this.businessService.findAllAsDataSource(fixedFilter, this.entityType, this.entityId, this.eventTypeIds);
    });
  }

  onSearch(): void {
    this.form.emitFilter();
  }

  onReset(): void {
    this.form.setValue({
      searchString: this.form.value.searchString,
      startDate: null,
      endDate: null,
    });
    this.form.emitFilter();
  }

  stringifyDateComponent(n: number): string {
    return padStart(toString(n), 2, '0');
  }

  isItemClickable(item: AelDto): boolean {
    const { eventTypeId } = item;
    let permission = '';
    if (this.eventTypeIdList.JOB.includes(eventTypeId)) {
      permission = 'JOB';
    } else if (this.eventTypeIdList.JOB_EST.includes(eventTypeId)) {
      permission = 'JOB_EST';
    } else if (this.eventTypeIdList.JOB_SPEC.includes(eventTypeId)) {
      permission = 'JOB_SPEC';
    } else if (this.eventTypeIdList.JOB_CS.includes(eventTypeId)) {
      permission = 'JOB_CS';
    }  else if (this.eventTypeIdList.CONTACT.includes(eventTypeId)) {
      permission = 'CONTACT';
    } else if (this.eventTypeIdList.LEAD.includes(eventTypeId)) {
      permission = 'LEAD';
    } else if (this.eventTypeIdList.SALES_FOLDER.includes(eventTypeId)) {
      permission = 'SALES_FOLDER';
    } else if (this.eventTypeIdList.QUOTE.includes(eventTypeId)) {
      permission = 'QUOTE';
    } else if (this.eventTypeIdList.HLP.includes(eventTypeId)) {
      permission = 'HLP';
    } else if (this.eventTypeIdList.PB.includes(eventTypeId)) {
      permission = 'PB';
    } else if (this.eventTypeIdList.HOUSE_PLAN.includes(eventTypeId)) {
      permission = 'HOUSE_PLAN';
    } else if (this.eventTypeIdList.SCHEDULES_SALES.includes(eventTypeId)) {
      permission = 'SCHEDULES_SALES';
    } else if (this.eventTypeIdList.SCHEDULES_MAINTENANCE.includes(eventTypeId)) {
      permission = 'SCHEDULES_MAINTENANCE';
    } else if (this.eventTypeIdList.SCHEDULES_CONSTRUCTOR.includes(eventTypeId)) {
      permission = 'SCHEDULES_CONSTRUCTOR';
    } else if (this.eventTypeIdList.SCHEDULES_PRE_CONSTRUCTOR.includes(eventTypeId)) {
      permission = 'SCHEDULES_PRE_CONSTRUCTOR';
    } else if (this.eventTypeIdList.JOB_VAR.includes(eventTypeId)) {
      permission = 'JOB_VAR';
    }
    return !!item.entityLink && !!this.ALL_ENTITY_TYPE_ID.includes(item.eventTypeId) &&
      (this.userContext.hasSystemRole(SystemRoleBit.Corporate) ||
      this.userContext.hasSystemRole(SystemRoleBit.Master) ||
      !!this.PERMISSIONS[permission]);
  }

  onItemClick(item: AelDto): void {
    if (item.entityLink && this.isItemClickable(item)) {
      const { entityLink, officeId } = item;
      let baseurl;
      if (this.userContext.masterView) {
        baseurl = officeId ? `${this.userContext.baseUrl.split('master-view')[0]}office-view/${officeId}` : this.userContext.baseUrl;
        openInThisWindow(replace(entityLink, '%BASE_URL%', baseurl));
        return;
      }
      if (this.userContext.corporateView) {
        baseurl = officeId ? `${this.userContext.baseUrl}office-view/${officeId}` : this.userContext.baseUrl;
        openInThisWindow(replace(entityLink, '%BASE_URL%', baseurl));
        return;
      }
      openInThisWindow(replace(entityLink, '%BASE_URL%', this.userContext.baseUrl));
    }
  }

  getLabel({ eventTypeId }: AelDto): string | void {
    if (this.eventTypeIdList.JOB.includes(eventTypeId)) {
      return 'lbl_open_job';
    }
    if (this.eventTypeIdList.JOB_VAR.includes(eventTypeId)) {
      return 'lbl_open_job_var';
    }
    if (this.eventTypeIdList.JOB_EST.includes(eventTypeId)) {
      return 'lbl_open_job_est';
    }
    if (this.eventTypeIdList.JOB_SPEC.includes(eventTypeId)) {
      return 'lbl_open_job_spec';
    }
    if (this.eventTypeIdList.JOB_CS.includes(eventTypeId)) {
      return 'lbl_open_job_cs';
    }
    if (
      this.eventTypeIdList.SCHEDULES_SALES.includes(eventTypeId) ||
      this.eventTypeIdList.SCHEDULES_MAINTENANCE.includes(eventTypeId) ||
      this.eventTypeIdList.SCHEDULES_CONSTRUCTOR.includes(eventTypeId) ||
      this.eventTypeIdList.SCHEDULES_PRE_CONSTRUCTOR.includes(eventTypeId)
    ) {
      return 'lbl_open_schedule';
    }
    if (this.eventTypeIdList.CONTACT.includes(eventTypeId)) {
      return 'lbl_open_contact_details';
    }
    if (this.eventTypeIdList.LEAD.includes(eventTypeId)) {
      return 'lbl_open_lead';
    }
    if (this.eventTypeIdList.SALES_FOLDER.includes(eventTypeId)) {
      return 'lbl_open_sf';
    }
    if (this.eventTypeIdList.QUOTE.includes(eventTypeId)) {
      return 'lbl_open_quote';
    }
    if (this.eventTypeIdList.HLP.includes(eventTypeId)) {
      return 'lbl_open_hlp';
    }
    if (this.eventTypeIdList.PB.includes(eventTypeId)) {
      return 'lbl_open_pb';
    }
    if (this.eventTypeIdList.HOUSE_PLAN.includes(eventTypeId)) {
      return 'lbl_open_hp';
    }
  }
}
